// connectivityDisplay — the CONNECTIVITY facet's shared display vocabulary
// (the patterns home the facet's build (ADR-063) lifts the tab pieces into; FRONTEND-PATTERNS
// §Entity surfaces): the derived path line, the per-service card, the SIM slot
// rows, and the revealable secret. The Connectivity tab, the Workbench stories
// and (at its own phase) the Devices entry's cells all mount THESE — variants
// arrive as props, never forks. Everything renders from the ONE field registry
// in `@ems-app/schemas/assets/access`, never per-protocol JSX.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  DEVICE_ACCESS_FIELDS,
  DEVICE_ACCESS_KINDS,
  DEVICE_ACCESS_KIND_GROUPS,
  DEVICE_ACCESS_PRIMARY_SECRET,
  WEB_SCHEME_DEFAULT_PORT,
  serviceKindsOfCapabilities,
  type DeviceAccessFieldDef,
  type DeviceAccessKind,
  type SimSlot,
} from '@ems-app/schemas/assets/access';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import type { AssetAccess, DeviceAccessViewEntry } from '@ems-app/schemas/assets/responses';

import { emDash } from './assetsDisplay.js';
import { DetailSection } from './DetailSection.js';
import { DescriptionList, Field } from './DescriptionList.js';
import { ObservationMark } from './observations.js';
import { Button } from '../ui/Button.js';
import { InlineCopy } from '../ui/CopyField.js';
import { Spinner } from '../ui/Spinner.js';
import { icons } from '../ui/icons.js';
import { formatNumber } from '../../lib/format.js';

// ── the tab set's role derivations ──────────────────────────────────────────

/** The disabled Usage direction-tab renders on categories that can be a
 *  telemetry SUBJECT (or a reporter with its own thin usage). PROVISIONAL
 *  approximation until the wiring-map binding dimension exists — the binding
 *  selects the real keys; today only the structural non-subjects are out:
 *  `cabinet` (structure) and `solar_panel` (the tracked member whose data
 *  rides its string). */
export function usageTabApplies(category: AssetCategory): boolean {
  return category !== 'cabinet' && category !== 'solar_panel';
}

// ── the derived path (Direct · Via X · Not declared) ────────────────────────

export function ConnectivityPathValue({
  access,
  quiet = false,
  compact = false,
}: {
  access: Pick<AssetAccess, 'access_path' | 'accessed_via'>;
  /** LIST face (the ruled list face / the contract-configs law): "Not declared"
   *  renders quiet — no mark, no tone — because the alarm lives in the
   *  Observations column. The tab keeps the loud default (it has no column). */
  quiet?: boolean;
  /** The degraded (phone/tight) face: glyph only, the words go sr-only. */
  compact?: boolean;
}) {
  const { t } = useTranslation('assets');
  if (access.access_path === 'via') {
    // A missing label = a scope-redacted target (the F-G law) — the path fact
    // stays true, the name yields the quiet dash.
    const label = t('device.path.via', { name: access.accessed_via?.label ?? '—' });
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5" title={compact ? label : undefined}>
        <icons.gateway size={15} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />
        {compact ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
      </span>
    );
  }
  if (access.access_path === 'direct') {
    const label = t('device.path.direct');
    return (
      <span className="inline-flex items-center gap-1.5" title={compact ? label : undefined}>
        <icons.devices size={15} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />
        {compact ? <span className="sr-only">{label}</span> : label}
      </span>
    );
  }
  if (quiet || compact) {
    return (
      <span className="text-[var(--color-text-secondary)]" title={compact ? t('device.path.none') : undefined}>
        {compact ? <span className="sr-only">{t('device.path.none')}</span> : t('device.path.none')}
        {compact && <span aria-hidden>—</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[var(--color-text-secondary)]">
      <ObservationMark kind="missing" impact="high" bare />
      {t('device.path.none')}
    </span>
  );
}

// ── the Services cell (the Devices entry's two-part cell — the ruled grammar, 2026-08-31) ─────
// The declared POLLING door as a word ("SNMP v2c" / "Modbus RTU" — the
// operationally loaded fact) + the declared MANAGEMENT doors as a fixed-order
// glyph rail (marks survive translation; words don't). Declared kinds only —
// no capability ghosts (the identity cell already names the product); no
// credential ink (the Observations column owns the alarm); vpn/cellular never
// appear (address facts, not doors — the peek carries them). Nothing declared
// = the quiet em-dash. A second polling door (rare) joins the rail as the
// polling glyph, its word on the tooltip.

const MANAGEMENT_GLYPHS: Partial<Record<DeviceAccessKind, typeof icons.serviceWeb>> = {
  web: icons.serviceWeb,
  ssh: icons.serviceSsh,
  ftp: icons.serviceFtp,
};

function ServiceGlyph({ glyph: Glyph, label }: { glyph: typeof icons.serviceWeb; label: string }) {
  return (
    <span className="inline-flex shrink-0" title={label}>
      <Glyph size={14} aria-hidden className="text-[var(--color-text-secondary)]" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function ServicesCellValue({
  kinds,
  compact = false,
}: {
  /** The row's DECLARED kinds, registry order (the wire's `connectivity.kinds`). */
  kinds: readonly DeviceAccessKind[];
  /** The degraded face: the polling word folds to its glyph — pure rail. */
  compact?: boolean;
}) {
  const { t } = useTranslation('assets');
  const polling = kinds.filter((k) => (DEVICE_ACCESS_KIND_GROUPS.polling as readonly DeviceAccessKind[]).includes(k));
  const management = kinds.filter((k) => MANAGEMENT_GLYPHS[k]);
  if (polling.length === 0 && management.length === 0) return emDash;
  const railPolling = compact ? polling : polling.slice(1);
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {!compact && polling[0] && <span className="truncate">{t(`device.kinds.${polling[0]}`)}</span>}
      {railPolling.map((k) => (
        <ServiceGlyph key={k} glyph={icons.servicePolling} label={t(`device.kinds.${k}`)} />
      ))}
      {management.map((k) => (
        <ServiceGlyph key={k} glyph={MANAGEMENT_GLYPHS[k]!} label={t(`device.kinds.${k}`)} />
      ))}
    </span>
  );
}

// ── the service split (used vs available) ───────────────────────────────────

/** The split every surface reads: the services/reach kinds the unit USES (an
 *  entry exists) vs the product's other capabilities (available, unused). A
 *  used service outside the product's datasheet is flagged, never hidden.
 *  Cellular never appears here — the SIM rows are its face. */
export function serviceSplit(access: Pick<AssetAccess, 'model' | 'entries'>): {
  used: { kind: DeviceAccessKind; entry: DeviceAccessViewEntry; fromProduct: boolean }[];
  available: DeviceAccessKind[];
} {
  const product = access.model ? serviceKindsOfCapabilities(access.model.capabilities) : [];
  const used = DEVICE_ACCESS_KINDS.flatMap((kind) => {
    if (kind === 'cellular') return [];
    const entry = access.entries.find((e) => e.kind === kind);
    return entry ? [{ kind, entry, fromProduct: product.includes(kind) }] : [];
  });
  const available = product.filter((k) => !access.entries.some((e) => e.kind === k));
  return { used, available };
}

/** The dialed port a blank port field means — the kind's standard (web's
 *  derives from the entry's scheme). null = the kind has no port. */
export function defaultPortOf(kind: DeviceAccessKind, entry?: Pick<DeviceAccessViewEntry, 'fields'>): number | null {
  if (kind === 'web') {
    const scheme = entry?.fields.scheme === 'https' ? 'https' : 'http';
    return WEB_SCHEME_DEFAULT_PORT[scheme];
  }
  return DEVICE_ACCESS_FIELDS[kind].find((f) => f.key === 'port')?.defaultValue ?? null;
}

// ── one revealed-on-demand secret (stars · Show/Hide — NO timer) ────────────
// ADR-063 rev: the controls are the per-look audit + the route's per-actor
// brake — no countdown, no step-up. The value is copyable ONLY once revealed
// (InlineCopy composes in; no copy affordance while masked).

type SecretState = { kind: 'hidden' } | { kind: 'pending' } | { kind: 'shown'; value: string } | { kind: 'error' };

export function RevealableSecret({
  set,
  primaryMissing,
  onReveal,
}: {
  /** A sealed value exists (the secrets_set marker). */
  set: boolean;
  /** The kind's primary secret is unset — where the credential-gap finding sits. */
  primaryMissing: boolean;
  /** The audited per-field reveal — absent = the viewer has no reveal power. */
  onReveal?: () => Promise<string>;
}) {
  const { t } = useTranslation('assets');
  const [state, setState] = useState<SecretState>({ kind: 'hidden' });

  if (!set) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[var(--color-text-secondary)]">
        {primaryMissing && <ObservationMark kind="missing" impact="high" bare />}
        {t('device.secret.notSet')}
      </span>
    );
  }

  if (state.kind === 'shown') {
    return (
      <span className="inline-flex max-w-full flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
        <InlineCopy value={state.value} iconAlways />
        <Button variant="link" tone="neutral" onClick={() => setState({ kind: 'hidden' })}>
          <icons.hide size={14} aria-hidden />
          {t('device.secret.hide')}
        </Button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden className="tracking-widest text-[var(--color-text-secondary)]">
        ••••••••
      </span>
      <span className="sr-only">{t('device.secret.set')}</span>
      {onReveal &&
        (state.kind === 'pending' ? (
          <Spinner size={16} />
        ) : (
          <Button
            variant="link"
            onClick={async () => {
              setState({ kind: 'pending' });
              try {
                const value = await onReveal();
                setState({ kind: 'shown', value });
              } catch {
                setState({ kind: 'error' });
              }
            }}
          >
            <icons.reveal size={14} aria-hidden />
            {t('device.secret.show')}
          </Button>
        ))}
      {state.kind === 'error' && <span className="text-xs text-[var(--color-danger)]">{t('device.secret.revealFailed')}</span>}
    </span>
  );
}

// ── one service card — the registry's fields, read; reach + credentials ONLY ─

export function ServiceCard({
  kind,
  entry,
  fromProduct,
  viaName,
  /** The product also declares `http-api` — the web card notes the API rides
   *  the same credentials (no per-unit API fields exist — ADR-063 rev). */
  apiNote = false,
  canReveal,
  onReveal,
}: {
  kind: DeviceAccessKind;
  entry: DeviceAccessViewEntry;
  fromProduct: boolean;
  /** Set when the unit is reached through another device — its ports are the
   *  ports dialed ON that device (the collector composes gateway + params). */
  viaName: string | null;
  apiNote?: boolean;
  canReveal: boolean;
  onReveal: (kind: DeviceAccessKind, field: string) => Promise<string>;
}) {
  const { t } = useTranslation('assets');
  const primary = DEVICE_ACCESS_PRIMARY_SECRET[kind];
  const secretSet = (key: string) => entry.secrets_set.includes(key);

  const label = (f: DeviceAccessFieldDef) =>
    f.key === 'port' && viaName ? t('device.fields.portOn', { name: viaName }) : t(`device.fields.${f.key}`);

  const value = (f: DeviceAccessFieldDef): ReactNode => {
    const v = entry.fields[f.key];
    if (f.kind === 'secret') {
      return (
        <RevealableSecret
          set={secretSet(f.key)}
          primaryMissing={primary === f.key && !secretSet(f.key)}
          onReveal={canReveal && secretSet(f.key) ? () => onReveal(kind, f.key) : undefined}
        />
      );
    }
    if (v === undefined || v === null || v === '') {
      if (f.key === 'port') {
        const def = defaultPortOf(kind, entry);
        // A forwarded port has no default — the protocol's own port is the
        // device's, not the gateway's.
        return def !== null && !viaName ? (
          <span dir="ltr" className="text-[var(--color-text-secondary)]">
            {t('device.portDefault', { port: String(def) })}
          </span>
        ) : (
          emDash
        );
      }
      if (f.key === 'local_port') {
        // blank local port = the kind's own standard inside (the remap case is
        // the exception, so the default reads quietly)
        const def = defaultPortOf(kind, entry);
        return def !== null ? (
          <span dir="ltr" className="text-[var(--color-text-secondary)]">
            {t('device.portDefault', { port: String(def) })}
          </span>
        ) : (
          emDash
        );
      }
      return emDash;
    }
    // Ports, unit ids and baud rates are identifiers — Western digits, never grouped (ADR-030).
    if (typeof v === 'number') return <span dir="ltr">{String(v)}</span>;
    if (f.kind === 'enum') return <span dir="ltr">{kind === 'vpn' ? t(`device.vpnFlavor.${v}`) : v.toUpperCase()}</span>;
    if (f.kind === 'url' || f.kind === 'ip' || f.kind === 'phone') return <InlineCopy value={v} iconAlways />;
    return <span dir="ltr">{v}</span>;
  };

  // `viaOnly` fields (local_port) render on Via units only — the registry's
  // own flag, never hand-wired per kind.
  const fields = DEVICE_ACCESS_FIELDS[kind].filter((f) => !f.viaOnly || viaName);

  return (
    <DetailSection
      title={t(`device.kinds.${kind}`)}
      action={
        <span className="text-xs text-[var(--color-text-secondary)]">
          {fromProduct ? t('device.fromProduct') : t('device.notInProduct')}
        </span>
      }
    >
      <DescriptionList>
        {fields.map((f) => (
          <Field key={f.key} label={label(f)}>
            {value(f)}
          </Field>
        ))}
      </DescriptionList>
      {apiNote && kind === 'web' && (
        <p className="mt-2 text-xs text-[var(--color-text-secondary)]">{t('device.webApiNote')}</p>
      )}
    </DetailSection>
  );
}

// ── the SIM slot rows (the cellular entry's face — slot records, no card) ────

export function simSlotsOf(access: Pick<AssetAccess, 'entries'>): SimSlot[] {
  const cell = access.entries.find((e) => e.kind === 'cellular');
  return [...(cell?.sims ?? [])].sort((a, b) => a.slot - b.slot);
}

export function SimSlotValue({ sim }: { sim: SimSlot | null }) {
  const { t } = useTranslation('assets');
  if (!sim || (!sim.imsi && !sim.msisdn && !sim.apn_ip && !sim.apn_ip_backup)) {
    return <span className="text-[var(--color-text-secondary)]">{t('device.sim.notFitted')}</span>;
  }
  const fact = (label: string, v: string | undefined) =>
    v ? (
      <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
        {label}
        <InlineCopy value={v} iconAlways />
      </span>
    ) : null;
  return (
    <span className="inline-flex max-w-full flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
      {sim.msisdn && <InlineCopy value={sim.msisdn} iconAlways />}
      {fact(t('device.sim.imsi'), sim.imsi)}
      {fact(t('device.sim.apn'), sim.apn_ip)}
      {fact(t('device.sim.apnBackup'), sim.apn_ip_backup)}
    </span>
  );
}

/** The reach section's SIM rows: one row per PRODUCT slot (the model's count),
 *  each showing its declared record or "Not fitted"; declared slots beyond the
 *  model's count still render (an honest record outranks a stale datasheet). */
export function SimSlotRows({ access, t }: { access: Pick<AssetAccess, 'model' | 'entries'>; t: TFunction<'assets'> }) {
  const declared = simSlotsOf(access);
  const slotCount = Math.max(access.model?.sims ?? 0, ...declared.map((s) => s.slot), 0);
  if (slotCount === 0) return null;
  return (
    <>
      {Array.from({ length: slotCount }, (_, i) => i + 1).map((n) => (
        <Field key={n} label={t('device.sim.label', { n: formatNumber(n) })}>
          <SimSlotValue sim={declared.find((s) => s.slot === n) ?? null} />
        </Field>
      ))}
    </>
  );
}
